import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs
} from '@chakra-ui/react';
import { CMXIcon } from '@codametrix/ui-components';
import React from 'react';

import type { ActivityFilter } from '../../interface';
import { useCodingScreen } from '../../interface';
import { readState } from '../../slots/readState';
import {
  type CaseNotesActivityTab,
  useCaseNotesActivityPanel
} from '../CaseNotesActivityPanelContext';

// The data-bound tab bodies are injected by the out-of-folder adapter via `panelContent` -- the
// isolation boundary forbids building them here (they need CoderNoteCard/Timeline/redux). See
// `interface/panelContent.ts`.
const TAB_ORDER: readonly CaseNotesActivityTab[] = ['notes', 'activity'];

const ACCENT = '#c96442';
const BORDER = '#ece6dd';
const MUTED = '#6b6258';

const asCount = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : 0;

export const CaseNotesActivityPanel: React.FC = () => {
  const { isOpen, activeTab, select, close } = useCaseNotesActivityPanel();
  const { config, state, panelContent, api } = useCodingScreen();
  const noteCount = asCount(readState(state, 'case.notesCount'));
  const activityCount = asCount(readState(state, 'case.activityCount'));

  // Grading bench has no coder notes to show, so only the Activity tab is offered; the Notes
  // tab/panel are dropped and Activity is forced active regardless of the panel's stored tab.
  const isGrading = config.mode === 'grading';
  const tabOrder = isGrading ? (['activity'] as const) : TAB_ORDER;
  const effectiveTab: CaseNotesActivityTab = isGrading ? 'activity' : activeTab;

  const activeIndex = Math.max(0, tabOrder.indexOf(effectiveTab));
  const notesActive = effectiveTab === 'notes';
  const activityActive = effectiveTab === 'activity';

  // Local-only selection (parity with `UserActivityComment`'s own `selectedFilter` state): this
  // folder has no redux, so it can't filter `panelContent.activity` itself -- a click just relays
  // the choice to the adapter via `api.onActivityFilterChange`, same division of labor as every
  // other adapter-injected action here (placeHold, onCodeSearch, ...).
  const [activityFilter, setActivityFilter] = React.useState<ActivityFilter>('all');
  const handleActivityFilterClick = (filter: ActivityFilter) => {
    setActivityFilter(filter);
    api?.onActivityFilterChange?.(filter);
  };

  const countPill = (count: number, active: boolean, testId: string) => (
    <Box
      as="span"
      data-testid={testId}
      px="6px"
      py="1px"
      borderRadius="999px"
      fontSize="10px"
      fontWeight={700}
      bg={active ? '#fbe9e1' : '#fafaf7'}
      color={active ? ACCENT : MUTED}
    >
      {count}
    </Box>
  );

  const filterChip = (filter: ActivityFilter, label: string) => {
    const active = activityFilter === filter;
    return (
      <Box
        as="button"
        type="button"
        key={filter}
        data-testid={`activity-filter-${filter}`}
        onClick={() => handleActivityFilterClick(filter)}
        aria-pressed={active}
        display="inline-flex"
        alignItems="center"
        gap="4px"
        px="10px"
        py="5px"
        borderRadius="999px"
        border={`1px solid ${active ? ACCENT : BORDER}`}
        bg={active ? '#fbe9e1' : 'transparent'}
        fontSize="11px"
        fontWeight={600}
        color={active ? ACCENT : MUTED}
      >
        {active && <CMXIcon svgIconName="BiomeCheckSVG20" svgIconSize="12" />}
        {label}
      </Box>
    );
  };

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={close}
      blockScrollOnMount={false}
    >
      <DrawerOverlay bg="rgba(20,18,16,0.22)"/>
      <DrawerContent
        data-testid="case-notes-activity-panel"
        maxW="460px"
        borderLeft={`1px solid ${BORDER}`}
        boxShadow="-24px 0 60px rgba(0,0,0,0.18)"
        sx={{ '& .chakra-modal__body': { padding: '0 !important' } }}
      >
        <DrawerCloseButton
          aria-label="Close panel"
          top="14px"
          right="20px"
          w="28px"
          h="28px"
          borderRadius="6px"
          bg="#fafaf7"
          border={`1px solid ${BORDER}`}
          color={MUTED}
        />
        <DrawerHeader
          px="20px"
          pt="16px"
          pb="12px"
          borderBottom={`1px solid ${BORDER}`}
          fontSize="15px"
          fontWeight={700}
          color="#2a2a2a"
        >
          {isGrading ? 'Activity' : 'Notes & Activity'}
        </DrawerHeader>
        <DrawerBody>
          <Tabs
            index={activeIndex}
            onChange={(i) => select(tabOrder[i] ?? tabOrder[0])}
            variant="unstyled"
            isLazy
            display="flex"
            flexDirection="column"
            h="100%"
          >
            <TabList px="14px" gap="2px" borderBottom={`1px solid ${BORDER}`}>
              {!isGrading && (
                <Tab
                  data-testid="case-notes-tab"
                  px="14px"
                  py="11px"
                  mb="-1px"
                  fontSize="12px"
                  fontWeight={600}
                  color={MUTED}
                  gap="6px"
                  borderBottom="2px solid transparent"
                  _selected={{ color: ACCENT, borderBottomColor: ACCENT }}
                >
                  Notes
                  {noteCount > 0 &&
                    countPill(noteCount, notesActive, 'case-notes-count')}
                </Tab>
              )}
              <Tab
                data-testid="activity-tab"
                px="14px"
                py="11px"
                mb="-1px"
                fontSize="12px"
                fontWeight={600}
                color={MUTED}
                gap="6px"
                borderBottom="2px solid transparent"
                _selected={{ color: ACCENT, borderBottomColor: ACCENT }}
              >
                Activity
                {activityCount > 0 &&
                  countPill(activityCount, activityActive, 'activity-count')}
              </Tab>
            </TabList>
            <TabPanels flex="1" minH={0} overflowY="auto">
              {!isGrading && (
                <TabPanel data-testid="case-notes-panel" px="20px" py="16px">
                  {panelContent?.notes ?? null}
                </TabPanel>
              )}
              <TabPanel data-testid="activity-panel" px="20px" py="16px">
                <Box
                  data-testid="activity-filter-chips"
                  display="flex"
                  gap="8px"
                  mb="12px"
                >
                  {isGrading && filterChip('feedback', 'Feedback')}
                  {filterChip('comments', 'Comments')}
                  {filterChip('all', 'All')}
                </Box>
                {panelContent?.activity ?? null}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
